import { toast } from "sonner";
import { audioClock, audioOut } from "@/lib/audio";
import { api } from "@/lib/api";

const KEY = "jt-feedback-sounds";
const SET_KEY = "jt-feedback-set";
export const EVENT = "jt-feedback-sounds";
export const SOUND_SETS = [["chime", "Chime"], ["bell", "Bell"], ["drum", "Drum hit"], ["cheer", "Cheer"]];
export const feedbackOn = () => localStorage.getItem(KEY) !== "off";
export const feedbackSet = () => (SOUND_SETS.some(([id]) => id === localStorage.getItem(SET_KEY)) ? localStorage.getItem(SET_KEY) : "chime");
const notify = () => window.dispatchEvent(new Event(EVENT));
export const setFeedbackOn = (on) => { localStorage.setItem(KEY, on ? "on" : "off"); notify(); };
export const setFeedbackSet = (id) => { localStorage.setItem(SET_KEY, id); notify(); };

// Tiny synth helpers on the shared audio context.
const tone = (freq, at, dur, vol = 0.35, type = "triangle", glideTo) => {
  const ac = audioClock(), t = ac.currentTime + at;
  const o = ac.createOscillator(), g = ac.createGain();
  o.type = type; o.frequency.setValueAtTime(freq, t);
  if (glideTo) o.frequency.exponentialRampToValueAtTime(glideTo, t + dur);
  g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(vol, t + 0.008); g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  o.connect(g); g.connect(audioOut()); o.start(t); o.stop(t + dur + 0.05);
};
const noise = (at, dur, vol = 0.3, freq = 1800, q = 0.8, swell = false) => {
  const ac = audioClock(), t = ac.currentTime + at;
  const buf = ac.createBuffer(1, Math.ceil(ac.sampleRate * dur), ac.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < d.length; i += 1) d[i] = Math.random() * 2 - 1;
  const src = ac.createBufferSource(), f = ac.createBiquadFilter(), g = ac.createGain();
  src.buffer = buf; f.type = "bandpass"; f.frequency.value = freq; f.Q.value = q;
  g.gain.setValueAtTime(swell ? 0.0001 : vol, t);
  if (swell) g.gain.exponentialRampToValueAtTime(vol, t + dur * 0.35);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  src.connect(f); f.connect(g); g.connect(audioOut()); src.start(t);
};
const bell = (freq, at, dur, vol) => [[1, 1], [2.76, 0.4], [5.4, 0.18]].forEach(([m, v]) => tone(freq * m, at, dur * (1.1 - v * 0.5), vol * v, "sine"));

const SETS = {
  chime: { right: () => { tone(880, 0, 0.12, 0.35); tone(1320, 0.09, 0.18, 0.35); }, wrong: () => { tone(196, 0, 0.22, 0.4); tone(165, 0.15, 0.28, 0.4); } },
  bell: { right: () => bell(1046, 0, 1.1, 0.3), wrong: () => { bell(233, 0, 0.6, 0.35); bell(247, 0.02, 0.6, 0.25); } },
  drum: {
    right: () => { tone(220, 0, 0.12, 0.6, "sine", 150); noise(0, 0.12, 0.35, 2500); tone(300, 0.11, 0.14, 0.6, "sine", 200); noise(0.11, 0.18, 0.4, 3000); noise(0.22, 0.6, 0.18, 7000, 0.5); },
    wrong: () => { tone(110, 0, 0.25, 0.8, "sine", 45); tone(100, 0.2, 0.3, 0.8, "sine", 40); },
  },
  cheer: {
    right: () => { noise(0, 1.1, 0.35, 1300, 0.6, true); noise(0.05, 1.0, 0.25, 2600, 1.2, true); tone(600, 0.05, 0.5, 0.12, "sine", 1100); },
    wrong: () => { tone(420, 0, 0.7, 0.16, "sawtooth", 230); noise(0, 0.6, 0.12, 700, 0.8, true); },
  },
};

export const playRight = () => { if (feedbackOn()) SETS[feedbackSet()]?.right(); };
export const playWrong = () => { if (feedbackOn()) SETS[feedbackSet()]?.wrong(); };
export const playResult = (ok) => (ok ? playRight() : playWrong());
export const previewSet = (id) => SETS[id]?.right();

// A loud hand clap for rhythm examples (three quick bursts + short tail); not muted by the feedback toggle.
export const playClap = (vol = 1) => {
  noise(0, 0.03, 0.9 * vol, 1500, 0.9); noise(0.011, 0.03, 0.9 * vol, 1400, 0.9); noise(0.022, 0.16, 1.0 * vol, 1200, 0.8);
};

// Short fanfare for a perfect round: rising arpeggio then a held major chord.
export const playFanfare = (delay = 0.35, game) => {
  if (game) {
    api.post("/me/perfect", { game })
      .then(({ data }) => {
        if (!data.added) return;
        const tier = { 10: "bronze", 50: "silver", 100: "gold" }[data.stars];
        if (tier) toast.success(`You've won the ${tier} trophy for ${data.stars} perfect rounds!`);
        else toast.success(`Perfect round! You earned a star (${data.stars} in total)`);
      })
      .catch(() => {});
  }
  if (!feedbackOn()) return;
  [523.25, 659.25, 783.99].forEach((f, i) => tone(f, delay + i * 0.13, 0.16, 0.3, "square"));
  [523.25, 659.25, 783.99, 1046.5].forEach((f) => tone(f, delay + 0.42, 0.9, 0.18, "triangle"));
};
